// One-time plaintext secret reveal — a short blocking Modal task whose result
// must not be lost. The plaintext is returned exactly once by
// `POST /v1/api-keys`, held only in memory by the caller, and is never stored,
// never in a toast, never in the URL. Dismiss is EXPLICIT ONLY: Esc and the
// scrim are no-ops so a stray click cannot destroy the one chance to copy the
// key; the sole exit is the primary "Done" button.

import type { components } from "../api/schema";
import { Button, Copyable, Inline, KeyValue, Modal, Stack, Text } from "../ui";

export type CreatedApiKey = Pick<
  components["schemas"]["ApiKeyCreateResponse"],
  "agent_id" | "team_id" | "display_name" | "key_prefix" | "plaintext_key"
>;

export interface ApiKeySecretModalProps {
  createdKey: CreatedApiKey | null;
  onDone: () => void;
  // Pre-computed truthful lock label — the
  // modal owns nothing async and never resolves agent names itself.
  scopeLabel: string;
}

// §4a one-time-secret law: the result must not be lost, so Esc/scrim never
// dismiss. The Modal primitive wires both to onDismiss; this ignores them.
function ignoreImplicitDismiss(): void {
  // Explicit "Done" is the only dismiss path.
}

export function ApiKeySecretModal({
  createdKey,
  onDone,
  scopeLabel,
}: ApiKeySecretModalProps) {
  return (
    <Modal
      ariaLabel="API key created"
      isOpen={createdKey != null}
      onDismiss={ignoreImplicitDismiss}
    >
      {createdKey ? (
        <Stack gap={4} role="status">
          <Text>Shown once. Copy it now; it cannot be retrieved again.</Text>
          <KeyValue
            rows={[
              { label: "Name", value: createdKey.display_name },
              { label: "Scope", value: scopeLabel },
              { label: "Prefix", value: createdKey.key_prefix },
            ]}
          />
          <Copyable
            label="API key"
            truncate={false}
            value={createdKey.plaintext_key}
          />
          <Inline gap={3}>
            <Button type="button" onClick={onDone}>
              Done
            </Button>
          </Inline>
        </Stack>
      ) : null}
    </Modal>
  );
}
